// Stay motion: reveals [data-stay-reveal] elements as they scroll into view (styles in base.css,
// "Stay motion"). Only runs when layout/theme.liquid set the stay-motion class on <html>.
// - Each element reveals once.
// - Inside a [data-stay-stagger] group, elements that come into view together follow each other
//   by the stagger step (at most 6 steps, or the group's own number: data-stay-stagger="10" lets ten come one
//   by one; V6, the PDP soft chew base's ten labels). Everything else reveals on its own.
// - Elements already above the screen when the page loads (a reload further down, a link to an
//   anchor) are shown at once, without animating.
// - [data-stay-alive] on touch screens (no hover; with a mouse, hover does it in CSS): .is-alive when the element
//   is 60% in view, or fills half the screen's height if it is taller (scrolled to, or swiped in a carousel), so its
//   moment plays once; it comes off only when the element has left the screen completely, so small scrolls don't
//   replay it.
//   data-stay-alive="in-view": the same on every screen, not only touch, and again on each mouse hover (a pointer
//   that can hover) once its moment has come to rest; a hover while it plays doesn't restart it.
// - [data-stay-scrolly]: the step ([data-stay-scrolly-step], its value the index of its photo) crossing the middle of
//   the screen marks its photo ([data-stay-scrolly-frame]) .is-active; the crossfade is base.css. State only.
// - [data-stay-loop] on a muted, looping <video> with data-src (decoration over an image of its first frame): the
//   source is set once it is near the screen, and it plays while on screen, after its [data-stay-reveal] ancestor
//   has settled; it pauses off screen and while the mouse is over, or focus is in, its target (the closest
//   [data-stay-loop-target], else its parent). Its parent gets .is-playing once it plays, to fade it in. Never with
//   reduced motion, in the theme editor or without JS (this script doesn't run): the image stays, nothing loads.
//   V6, What to expect's dog.
// - Markup added later (theme dev's hot reload of a section, the theme editor, anything that inserts
//   HTML) is picked up too; otherwise it would stay hidden, since the page-wide class is already set.
(() => {
  const root = document.documentElement;
  if (!root.classList.contains('stay-motion')) return;
  window.StayMotion = true;

  const MAX_STEPS = 6;

  // is-settled ends the entrance: the reveal's transition and its stagger delay come off, so later state changes
  // (hover, focus, a pair highlight) start at once. Only the element's own transition counts: a child's
  // transitionend bubbles up first (an icon popping in finishes before its row), so the listener stays until
  // the element's own one ends, or is cancelled (a hover changing its opacity mid-entrance).
  const settle = (element) => {
    const done = (event) => {
      if (event.target !== element) return;
      element.classList.add('is-settled');
      element.removeEventListener('transitionend', done);
      element.removeEventListener('transitioncancel', done);
    };
    element.addEventListener('transitionend', done);
    element.addEventListener('transitioncancel', done);
  };

  const reveal = (entries, observer) => {
    const steps = new Map();
    entries
      .filter((entry) => entry.isIntersecting)
      .map((entry) => entry.target)
      .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
      .forEach((element) => {
        const group = element.closest('[data-stay-stagger]');
        const step = group ? steps.get(group) || 0 : 0;
        if (group) steps.set(group, step + 1);
        const maxSteps = (group && Number(group.dataset.stayStagger)) || MAX_STEPS;
        element.style.setProperty('--stay-reveal-step', Math.min(step, maxSteps));
        settle(element);
        element.classList.add('is-revealed');
        observer.unobserve(element);
      });
  };

  // Reveals start when an element is 10% up the screen; "drive" waits until its frame's top is 30% up,
  // so the section is well in view before the cut-out rolls in.
  const observer = new IntersectionObserver(reveal, { rootMargin: '0px 0px -10% 0px' });
  const lateObserver = new IntersectionObserver(reveal, { rootMargin: '0px 0px -30% 0px' });

  const noHover = window.matchMedia('(hover: none)');
  const aliveObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, isIntersecting, boundingClientRect: box, intersectionRect: seen }) => {
        if (!noHover.matches && target.dataset.stayAlive !== 'in-view') return;
        const across = seen.width >= box.width * 0.6;
        const down = seen.height >= Math.min(box.height * 0.6, window.innerHeight / 2);
        if (across && down) target.classList.add('is-alive');
        else if (!isIntersecting) target.classList.remove('is-alive');
      });
    },
    { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8] }
  );

  const canHover = window.matchMedia('(hover: hover)');
  const replay = (event) => {
    const element = event.currentTarget;
    if (event.pointerType !== 'mouse' || !canHover.matches) return;
    if (element.getAnimations({ subtree: true }).some((animation) => animation.playState === 'running')) return;
    element.classList.remove('is-alive');
    void element.offsetWidth;
    element.classList.add('is-alive');
  };

  const watchAlive = (element) => {
    aliveObserver.observe(element);
    if (element.dataset.stayAlive === 'in-view') element.addEventListener('pointerenter', replay);
  };

  const scrolly = (container) => {
    if (container.classList.contains('is-scrolly')) return;
    const frames = [...container.querySelectorAll('[data-stay-scrolly-frame]')];
    const steps = container.querySelectorAll('[data-stay-scrolly-step]');
    if (frames.length < 2 || !steps.length) return;
    container.classList.add('is-scrolly');
    const show = (index) => frames.forEach((frame, i) => frame.classList.toggle('is-active', i === index));
    show(0);
    const middle = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) show(Number(entry.target.dataset.stayScrollyStep) || 0);
        });
      },
      { rootMargin: '-50% 0px -50% 0px' }
    );
    steps.forEach((step) => middle.observe(step));
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const loops = new Map();
  const loopObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        const loop = loops.get(target);
        loop.inView = isIntersecting;
        loop.update();
      });
    },
    { threshold: 0 }
  );
  const loopNearObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) return;
        loopNearObserver.unobserve(target);
        loops.get(target).load();
      });
    },
    { rootMargin: '300px' }
  );

  const loopVideo = (video) => {
    if (loops.has(video)) return;
    const target = video.closest('[data-stay-loop-target]') || video.parentElement;
    const reveal = video.closest('[data-stay-reveal]');
    const loop = {
      inView: false,
      held: false,
      load() {
        if (reducedMotion.matches || video.getAttribute('src')) return;
        video.src = video.dataset.src;
        video.preload = 'auto';
        video.load();
      },
      update() {
        const settled = !reveal || reveal.classList.contains('is-settled');
        if (loop.inView && settled && !loop.held && !reducedMotion.matches) {
          loop.load();
          // Autoplay can be blocked (e.g. low power mode); the image under it then stays
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
    };
    loops.set(video, loop);

    video.addEventListener('playing', () => video.parentElement.classList.add('is-playing'));
    const hold = (held) => () => {
      loop.held = held;
      loop.update();
    };
    target.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && hold(true)());
    target.addEventListener('pointerleave', (event) => event.pointerType === 'mouse' && hold(false)());
    target.addEventListener('focusin', hold(true));
    target.addEventListener('focusout', (event) => {
      if (!target.contains(event.relatedTarget)) hold(false)();
    });
    reducedMotion.addEventListener('change', loop.update);
    if (reveal && !reveal.classList.contains('is-settled')) {
      const settledObserver = new MutationObserver(() => {
        if (!reveal.classList.contains('is-settled')) return;
        settledObserver.disconnect();
        loop.update();
      });
      settledObserver.observe(reveal, { attributes: true, attributeFilter: ['class'] });
    }
    loopNearObserver.observe(video);
    loopObserver.observe(video);
  };

  const track = (element) => {
    if (element.classList.contains('is-revealed')) return;
    if (element.getBoundingClientRect().bottom <= 0) {
      element.classList.add('is-revealed', 'is-settled');
    } else {
      (element.dataset.stayReveal === 'drive' ? lateObserver : observer).observe(element);
    }
  };

  const init = (scope) => {
    if (scope.matches?.('[data-stay-reveal]')) track(scope);
    scope.querySelectorAll('[data-stay-reveal]').forEach(track);
    if (scope.matches?.('[data-stay-alive]')) watchAlive(scope);
    scope.querySelectorAll('[data-stay-alive]').forEach(watchAlive);
    if (scope.matches?.('[data-stay-scrolly]')) scrolly(scope);
    scope.querySelectorAll('[data-stay-scrolly]').forEach(scrolly);
    if (scope.matches?.('[data-stay-loop]')) loopVideo(scope);
    scope.querySelectorAll('[data-stay-loop]').forEach(loopVideo);
  };

  init(document);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) init(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
